import { useState, useCallback } from 'react';
import {
  Pane, Heading, Text, Button, Badge, SelectMenu, Spinner,
  toaster, TickCircleIcon, DownloadIcon, TrashIcon, Paragraph, majorScale,
} from 'evergreen-ui';
import pagamentiService from '../../services/pagamentiService';
import FileUploaderMultipleUploadDemo from '../../commons/FileUploaderMultipleUploadDemo';

const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const currentYear = new Date().getFullYear();
const ANNI = [currentYear - 1, currentYear, currentYear + 1];

const PagamentiDirettore = ({ collaborators }) => {
  const [selectedCollaboratore, setSelectedCollaboratore] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadForm, setUploadForm] = useState({
    tipo: 'busta_paga',
    mese: new Date().getMonth() + 1,
    anno: currentYear,
  });
  const [uploaderKey, setUploaderKey] = useState(0);

  const loadDocuments = useCallback(async (collaboratoreId) => {
    setLoadingDocs(true);
    try {
      const data = await pagamentiService.getCollaboratoreDocuments(collaboratoreId);
      setDocuments(data || []);
    } catch {
      toaster.danger('Errore nel caricamento dei documenti.');
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  const handleSelectCollaboratore = (collaboratore) => {
    setSelectedCollaboratore(collaboratore);
    setDocuments([]);
    loadDocuments(collaboratore.id);
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      toaster.warning('Seleziona almeno un file.');
      return;
    }
    setUploading(true);
    try {
      const mese = uploadForm.tipo === 'busta_paga' ? uploadForm.mese : null;
      await pagamentiService.uploadDocuments(
        uploadFiles,
        selectedCollaboratore.id,
        uploadForm.tipo,
        mese,
        uploadForm.anno
      );
      toaster.success('Documenti caricati con successo.');
      setUploadFiles([]);
      setUploaderKey((k) => k + 1);
      loadDocuments(selectedCollaboratore.id);
    } catch {
      toaster.danger('Errore durante il caricamento.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentoId) => {
    try {
      await pagamentiService.deleteDocument(documentoId);
      setDocuments((prev) => prev.filter((d) => d.id !== documentoId));
      toaster.success('Documento eliminato.');
    } catch {
      toaster.danger("Errore durante l'eliminazione.");
    }
  };

  const handleDownload = (doc) => {
    const url = pagamentiService.getDownloadUrl(doc.id);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.file_name;
    a.click();
  };

  const collaboratoreOptions = (collaborators || []).map((c) => ({
    label: `${c.nome} ${c.cognome}`,
    value: c.id,
  }));

  const selectedLabel = selectedCollaboratore
    ? `${selectedCollaboratore.nome} ${selectedCollaboratore.cognome}`
    : 'Seleziona un collaboratore';

  const renderDoc = (doc) => (
    <Pane
      key={doc.id}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      padding={12}
      marginBottom={8}
      border="1px solid #E6E8F0"
      borderRadius={4}
      background="white"
    >
      <Pane>
        <Text fontWeight={500}>{doc.file_name}</Text>
        {doc.tipo === 'busta_paga' && doc.mese && doc.anno && (
          <Text size={300} color="muted" marginLeft={8}>
            {MESI[doc.mese - 1]} {doc.anno}
          </Text>
        )}
        <Pane display="flex" alignItems="center" marginTop={4}>
          {doc.letto_at ? (
            <>
              <TickCircleIcon color="success" size={12} marginRight={4} />
              <Text size={300} color="muted">
                Letto il {new Date(doc.letto_at).toLocaleString('it-IT')}
              </Text>
            </>
          ) : (
            <Badge color="red">Non letto</Badge>
          )}
        </Pane>
      </Pane>
      <Pane display="flex" gap={8}>
        <Button iconBefore={DownloadIcon} appearance="minimal" onClick={() => handleDownload(doc)}>
          Scarica
        </Button>
        <Button
          iconBefore={TrashIcon}
          appearance="minimal"
          intent="danger"
          onClick={() => handleDelete(doc.id)}
        >
          Elimina
        </Button>
      </Pane>
    </Pane>
  );

  const bustePaga = documents.filter((d) => d.tipo === 'busta_paga');
  const bonifici = documents.filter((d) => d.tipo === 'bonifico');

  return (
    <Pane>
      <Heading size={600} marginBottom={16}>Gestione pagamenti</Heading>

      <Pane marginBottom={24}>
        <SelectMenu
          title="Seleziona collaboratore"
          options={collaboratoreOptions}
          selected={selectedCollaboratore?.id}
          onSelect={(item) => {
            const c = (collaborators || []).find((x) => x.id === item.value);
            if (c) handleSelectCollaboratore(c);
          }}
        >
          <Button>{selectedLabel}</Button>
        </SelectMenu>
      </Pane>

      {selectedCollaboratore && (
        <>
          {/* Form di upload */}
          <Pane
            background="tint1"
            padding={16}
            borderRadius={4}
            marginBottom={24}
            border="1px solid #E6E8F0"
          >
            <Heading size={400} marginBottom={12}>
              Carica documenti per {selectedCollaboratore.nome} {selectedCollaboratore.cognome}
            </Heading>

            <Pane display="flex" gap={majorScale(2)} marginBottom={12} flexWrap="wrap" alignItems="flex-end">
              <Pane>
                <Text size={300} fontWeight={500} display="block" marginBottom={4}>Tipo</Text>
                <select
                  value={uploadForm.tipo}
                  onChange={(e) => setUploadForm((f) => ({ ...f, tipo: e.target.value }))}
                  style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #C1C4D6' }}
                >
                  <option value="busta_paga">Busta paga</option>
                  <option value="bonifico">Bonifico</option>
                </select>
              </Pane>

              {uploadForm.tipo === 'busta_paga' && (
                <Pane>
                  <Text size={300} fontWeight={500} display="block" marginBottom={4}>Mese</Text>
                  <select
                    value={uploadForm.mese}
                    onChange={(e) => setUploadForm((f) => ({ ...f, mese: Number(e.target.value) }))}
                    style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #C1C4D6' }}
                  >
                    {MESI.map((m, i) => (
                      <option key={i} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </Pane>
              )}

              <Pane>
                <Text size={300} fontWeight={500} display="block" marginBottom={4}>Anno</Text>
                <select
                  value={uploadForm.anno}
                  onChange={(e) => setUploadForm((f) => ({ ...f, anno: Number(e.target.value) }))}
                  style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #C1C4D6' }}
                >
                  {ANNI.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </Pane>
            </Pane>

            <FileUploaderMultipleUploadDemo key={uploaderKey} onFilesChange={setUploadFiles} />

            <Button
              appearance="primary"
              marginTop={12}
              onClick={handleUpload}
              isLoading={uploading}
              disabled={uploadFiles.length === 0}
            >
              Carica documenti
            </Button>
          </Pane>

          {/* Lista documenti */}
          {loadingDocs ? (
            <Spinner />
          ) : (
            <>
              <Heading size={500} marginBottom={12}>Buste paga</Heading>
              {bustePaga.length === 0 ? (
                <Paragraph color="muted" marginBottom={24}>Nessuna busta paga caricata.</Paragraph>
              ) : (
                <Pane marginBottom={24}>{bustePaga.map(renderDoc)}</Pane>
              )}

              <Heading size={500} marginBottom={12}>Bonifici</Heading>
              {bonifici.length === 0 ? (
                <Paragraph color="muted">Nessun bonifico caricato.</Paragraph>
              ) : (
                <Pane>{bonifici.map(renderDoc)}</Pane>
              )}
            </>
          )}
        </>
      )}
    </Pane>
  );
};

export default PagamentiDirettore;
